import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import Page from "~Common/Page";
import CustomSelect from "~components/CustomSelect";
import { CompetitorTypes } from "~enums/competitorTypes";
import { FixtureStatuses } from "~enums/fixtureStatuses";
import { ImageTypes } from "~enums/imageTypes";
import { useFetchSeason } from "~hooks/apiHooks/useFetchSeason.ts";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import styles from "~pages/Roster/components/AddPlayerToRoster/components/AddPersonRolesModalFilters.module.css";
import RosterTable from "~pages/Roster/components/RosterTable/RosterTable";
import useDeleteRosterPlayer from "~pages/Roster/hooks/useDeleteRosterPlayer";
import useFetchDefaultFixtureProfile from "~pages/Roster/hooks/useFetchDefaultFixtureProfile";
import useFetchEntity from "~pages/Roster/hooks/useFetchEntity.ts";
import { useGetFixture } from "~pages/Roster/hooks/useFetchFixture";
import { useFetchInternationalGamesAmount } from "~pages/Roster/hooks/useFetchInternationalGamesAmount.ts";
import useFetchRoster from "~pages/Roster/hooks/useFetchRoster";
import { useGetSeasonFixtureProfile } from "~pages/Roster/hooks/useGetSeasonFixtureProfile";
import useUpsertRosterPlayer from "~pages/Roster/hooks/useUpsertRosterPlayer";
import type { UpsertFixtureRosterModel } from "~pages/Roster/types/upsertFixtureRosterModel";
import type { SeasonResponse } from "~types/season.ts";
import { findImageByType } from "~utilities/findImageByType";

const FixtureRosterPage = () => {
  const { fixtureId, seasonId, sport } = useParams();
  const { competitorType, uiConfiguration } = useSportConfiguration();

  if (competitorType === CompetitorTypes.Person) {
    throw Error("Fixture roster does not support for individual sports");
  }

  const { formatMessage } = useIntl();

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(1000);

  const params: Record<string, string> = {
    include: ["entities", "seasons", "persons", "fixture"].join(","),
    limit: "1000",
    offset: "0",
  };

  const [selectedEntrant, setSelectedEntrant] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [entrantSelectorOptions, setEntrantSelectorOptions] = useState<{ value: string; label: string }[]>([]);

  const { data: roster, isLoading: isRosterLoading } = useFetchRoster({
    customParams: params,
    entityId: selectedEntrant?.value,
    fixtureId,
  });
  const { data: fixture, isLoading: isFixtureLoading } = useGetFixture();
  const { seasonFixtureProfile, isSeasonLoading } = useGetSeasonFixtureProfile();
  const { data: defaultFixtureProfile, isLoading: isDefaultProfileLoading } = useFetchDefaultFixtureProfile();

  const isRosterEditable =
    !!fixture &&
    ![FixtureStatuses.Bye, FixtureStatuses.Confirmed, FixtureStatuses.Abandoned, FixtureStatuses.Cancelled].includes(fixture.status);

  const { data: entity } = useFetchEntity(selectedEntrant?.value);

  const { data: season } = useFetchSeason(seasonId, {}, (seasonData: SeasonResponse) => {
    return seasonData.data[0] || {};
  });

  const { data: intGamesAmount, isLoading: isIntGamesAmountPending } = useFetchInternationalGamesAmount({
    representing: entity?.representing,
    seasonId: seasonId,
    discipline: season?.discipline,
  });

  const rosterTableData = roster?.map((item) => {
    return {
      ...item,
      nameFullLocal: item.person.nameFullLocal,
      avatarUrl: findImageByType(item.person?.images, ImageTypes.PersonHead),
      personId: item.personId,
      dob: item.person.dob,
      status: item?.status,
      position: item.position,
      isCaptain: item.isCaptain,
      captainRoleId: item.captainRoleId,
      bib: item.bib,
      starter: item.starter,
      caps: intGamesAmount ? intGamesAmount[item.personId] : "",
    };
  });

  const maxRosterSize =
    fixture?.fixtureProfile?.profile.maximumRosterSize ||
    seasonFixtureProfile?.profile.maximumRosterSize ||
    defaultFixtureProfile?.profile.maximumRosterSize;
  const maxStarterSize =
    fixture?.fixtureProfile?.profile.maximumPlayersOnCourt ||
    seasonFixtureProfile?.profile.maximumPlayersOnCourt ||
    defaultFixtureProfile?.profile.maximumPlayersOnCourt;

  const { mutateAsync: upsertRoster } = useUpsertRosterPlayer();

  const handleUpsert = async (value: UpsertFixtureRosterModel) => {
    if (fixtureId && selectedEntrant?.value) {
      await upsertRoster({
        ...value,
        fixtureId,
        entityId: selectedEntrant?.value,
      });
    } else {
      throw Error("Please specify fixtureId and entityId");
    }
  };

  const { mutateAsync: deleteRosterPlayer, isPending: isRosterDeleting } = useDeleteRosterPlayer();

  const handleDelete = async (value: string) => {
    await deleteRosterPlayer(value);
  };

  useEffect(() => {
    const entrantSelectorOptions = (fixture?.competitors || []).map((item) => ({
      label: item.name,
      value: item.id,
    }));

    setEntrantSelectorOptions(entrantSelectorOptions);
    setSelectedEntrant(entrantSelectorOptions[0] || null);
  }, [fixture]);

  const isLoading =
    isRosterLoading || isSeasonLoading || isDefaultProfileLoading || isFixtureLoading || isRosterDeleting || isIntGamesAmountPending;

  const title = fixture ? fixture.fixtureName : "";

  return (
    <Page title={`${title ?? ""} ${formatMessage({ id: "roster.fixture" })}`}>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <CustomSelect
          onChange={(_: unknown, value: unknown) => {
            setSelectedEntrant(value as { value: string; label: string });
          }}
          options={entrantSelectorOptions}
          value={selectedEntrant}
          placeholder={formatMessage({ id: "status" })}
          className={styles.select}
          disabled={isLoading}
        />
        <RosterTable
          isRosterEditable={isRosterEditable}
          handleDelete={handleDelete}
          handleUpsert={handleUpsert}
          queryKey={"fixtureRoster"}
          entityId={selectedEntrant?.value}
          isLoading={isLoading}
          data={rosterTableData || []}
          pagination={{
            currentPage,
            pageSize,
            pageSizeOptions,
            handlePaginationChange,
            offset,
          }}
          maxRosterSize={maxRosterSize}
          maxStarterSize={maxStarterSize}
          columnSettingsLocalStorageKey={`${sport}.tables.settings.columns.fixture.roster`}
          columnSortLocalStorageKey={`${sport}.tables.settings.sort.fixture.roster`}
          tableConfiguration={uiConfiguration.pages.fixtureRoster.tableColumnGroups}
        />
      </div>
    </Page>
  );
};

export default FixtureRosterPage;
